/**
 * Analytics Tracking Utilities for Public Menu
 * Sends pageview and interaction events to backend
 * Respects cookie consent preferences
 */

import { isCookieAllowed } from '../components/CookieBanner';

// Intelligent API base URL resolution - same as PublicMenu.jsx
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // In development, frontend is on 3000 but backend might be on different port
  // Use relative URL to let the proxy handle it, or same origin
  const protocol = window.location.protocol;
  const host = window.location.host;
  return `${protocol}//${host}`;
};

// Use relative path to work with CRA proxy in development
const API = '/api/analytics';

// Queue for batching events (optional optimization)
let eventQueue = [];
let flushTimeout = null;

/**
 * Track a page view event
 * Only tracks if analytics cookies are allowed
 */
export const trackPageView = async (tenantId, page, options = {}) => {
  if (!tenantId) return;
  
  // Check if analytics cookies are allowed
  if (!isCookieAllowed('analytics')) {
    console.debug('Analytics pageview skipped - consent not given');
    return;
  }

  const payload = {
    tenant_id: tenantId,
    page: page, // 'menu', 'item_detail', 'category'
    item_id: options.itemId || null,
    category_id: options.categoryId || null,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent || null,
    language: navigator.language || null,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight
  };

  try {
    const response = await fetch(`${API}/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Don't block on analytics
      keepalive: true
    });
    if (!response.ok) {
      console.warn('Analytics pageview failed with status:', response.status);
    } else {
      console.debug('Analytics pageview tracked:', page, tenantId);
    }
  } catch (err) {
    // Silently fail - analytics should not break the menu
    console.warn('Analytics pageview failed:', err);
  }
};

/**
 * Track a user interaction event
 * Only tracks if analytics cookies are allowed
 */
export const trackInteraction = async (tenantId, action, options = {}) => {
  if (!tenantId) return;
  
  // Check if analytics cookies are allowed
  if (!isCookieAllowed('analytics')) {
    console.debug('Analytics interaction skipped - consent not given');
    return;
  }

  const payload = {
    tenant_id: tenantId,
    action: action, // 'view_item', 'expand_category', 'search', 'filter', 'share', 'qr_scan'
    item_id: options.itemId || null,
    category_id: options.categoryId || null,
    search_query: options.searchQuery || null,
    filter_type: options.filterType || null
  };

  try {
    await fetch(`${API}/track/interaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
  } catch (err) {
    console.debug('Analytics interaction failed:', err);
  }
};

/**
 * React hook for menu analytics
 */
export const useMenuAnalytics = (tenantId) => {
  const trackPage = (page, options = {}) => {
    trackPageView(tenantId, page, options);
  };

  const trackAction = (action, options = {}) => {
    trackInteraction(tenantId, action, options);
  };

  return {
    trackPage,
    trackAction,
    // Convenience methods
    trackMenuView: () => trackPage('menu'),
    trackItemView: (itemId) => {
      trackPage('item_detail', { itemId });
      trackAction('view_item', { itemId });
    },
    trackCategoryView: (categoryId) => {
      trackPage('category', { categoryId });
      trackAction('expand_category', { categoryId });
    },
    trackSearch: (query) => trackAction('search', { searchQuery: query }),
    trackFilter: (filterType) => trackAction('filter', { filterType }),
    trackShare: (itemId) => trackAction('share', { itemId }),
    trackQrScan: () => trackAction('qr_scan')
  };
};
